import L from "leaflet";
import { measureSummary } from "../draw/Measure";
import type { MeasureMode } from "../draw/Measure";
import type { Labels } from "../i18n/labels";
import type { MeasureApi } from "./api";
import type { LatLngTuple } from "./options";
import type { MapStore } from "./store";

export const measurePane = "fxmap-measure";

export const measureColor = "#B4530A";

type Session = {
  mode: MeasureMode;
  points: LatLngTuple[];
  shape: L.Polyline | L.Polygon;
  rubber: L.Polyline;
  vertices: L.LayerGroup;
  cursor: L.Tooltip;
  doubleClickZoom: boolean;
};

export type MeasureController = MeasureApi & { active(): boolean; finish(): void; cancel(): void; handleKey(event: KeyboardEvent): boolean; dispose(): void };

export type MeasureControllerOptions = { map: L.Map; store: MapStore; labels: Labels };

function tupleOf(point: L.LatLng): LatLngTuple {
  return [point.lat, point.lng];
}

export function createMeasureController(options: MeasureControllerOptions): MeasureController {
  const { map, store, labels } = options;
  const pane = map.getPane(measurePane) ?? map.createPane(measurePane);

  pane.style.zIndex = "940";

  const renderer = L.svg({ pane: measurePane });
  const finished = L.layerGroup().addTo(map);
  let session: Session | null = null;
  let lastResult: string | null = null;

  const write = (points: LatLngTuple[], summary: string | null) => {
    store.setState({ measure: { mode: session?.mode ?? null, points: points.map(a => [a[0], a[1]]), summary } });
  };

  const readout = (points: LatLngTuple[], cursor: L.LatLng | null = null) => {
    if (session === null) return;

    const summary = points.length === 0 ? labels.measureHint : measureSummary(session.mode, points, labels);

    write(session.points, summary);

    if (cursor !== null && points.length >= 2) {
      session.cursor.setLatLng(cursor);
      session.cursor.setContent(summary);

      if (!map.hasLayer(session.cursor)) session.cursor.addTo(map);
    }
  };

  const cleanupSession = () => {
    if (session === null) return;

    const current = session;

    session = null;
    map.off("click", onClick);
    map.off("mousemove", onMove);
    map.off("dblclick", onDoubleClick);
    map.removeLayer(current.shape);
    map.removeLayer(current.rubber);
    map.removeLayer(current.vertices);

    if (map.hasLayer(current.cursor)) map.removeLayer(current.cursor);
    map.getContainer().classList.remove("fxmap-cursor-crosshair");

    if (current.doubleClickZoom) map.doubleClickZoom.enable();
  };

  const cancel = () => {
    if (session === null) return;

    cleanupSession();
    write([], lastResult);
  };

  const finish = () => {
    if (session === null) return;

    const current = session;
    const minimum = current.mode === "distance" ? 2 : 3;

    if (current.points.length < minimum) {
      cancel();
      return;
    }

    const text = measureSummary(current.mode, current.points, labels);
    const shape =
      current.mode === "distance"
        ? L.polyline(current.points, { pane: measurePane, renderer, color: measureColor, weight: 3, dashArray: "6 4", fill: false, className: "fxmap-measure-result fxmap-measure-result-line" })
        : L.polygon(current.points, { pane: measurePane, renderer, color: measureColor, weight: 2, fillColor: measureColor, fillOpacity: 0.12, className: "fxmap-measure-result fxmap-measure-result-area" });

    shape.bindTooltip(text, { permanent: true, direction: current.mode === "distance" ? "top" : "center", className: "fxmap-measure-label", pane: "tooltipPane" });
    shape.addTo(finished);
    lastResult = text;
    cleanupSession();
    write([], lastResult);
  };

  const clear = () => {
    cleanupSession();
    finished.clearLayers();
    lastResult = null;
    write([], null);
  };

  const onClick = (event: L.LeafletMouseEvent) => {
    if (session === null) return;

    const point = tupleOf(event.latlng);
    const previous = session.points[session.points.length - 1];

    if (previous && previous[0] === point[0] && previous[1] === point[1]) return;

    session.points.push(point);
    session.shape.setLatLngs(session.points);
    L.circleMarker(event.latlng, { pane: measurePane, renderer, radius: 4, color: measureColor, weight: 2, fillColor: "#ffffff", fillOpacity: 1, interactive: false }).addTo(session.vertices);
    session.rubber.setLatLngs([event.latlng, event.latlng]);
    readout(session.points);
  };

  const onMove = (event: L.LeafletMouseEvent) => {
    if (session === null) return;

    const last = session.points[session.points.length - 1];

    if (!last) return;

    const cursorPoint = tupleOf(event.latlng);

    session.rubber.setLatLngs([last, event.latlng]);
    readout([...session.points, cursorPoint], event.latlng);
  };

  const onDoubleClick = (event: L.LeafletMouseEvent) => {
    if (session === null) return;

    L.DomEvent.stop(event);
    finish();
  };

  const handleKey = (event: KeyboardEvent): boolean => {
    if (session === null) return false;

    if (event.key === "Escape") {
      event.preventDefault();
      cancel();
      return true;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      finish();
      return true;
    }

    return false;
  };

  const start = (mode: MeasureMode) => {
    cleanupSession();

    const doubleClickZoom = map.doubleClickZoom.enabled();

    map.doubleClickZoom.disable();

    session = {
      mode,
      points: [],
      shape:
        mode === "distance"
          ? L.polyline([], { pane: measurePane, renderer, color: measureColor, weight: 3, fill: false, className: "fxmap-measure-line" }).addTo(map)
          : L.polygon([], { pane: measurePane, renderer, color: measureColor, weight: 2, fillColor: measureColor, fillOpacity: 0.12, className: "fxmap-measure-area" }).addTo(map),
      rubber: L.polyline([], { pane: measurePane, renderer, color: measureColor, weight: 2, dashArray: "4 4", interactive: false, className: "fxmap-measure-rubber" }).addTo(map),
      vertices: L.layerGroup().addTo(map),
      cursor: L.tooltip({ permanent: true, direction: "right", offset: [12, 0], className: "fxmap-measure-label", pane: "tooltipPane", interactive: false }),
      doubleClickZoom
    };

    map.getContainer().classList.add("fxmap-cursor-crosshair");
    map.on("click", onClick);
    map.on("mousemove", onMove);
    map.on("dblclick", onDoubleClick);
    write([], labels.measureHint);
  };

  return {
    start,
    stop: cancel,
    clear,
    finish,
    cancel,
    active: () => session !== null,
    handleKey,
    dispose: () => {
      clear();

      if (map.hasLayer(finished)) map.removeLayer(finished);
    }
  };
}
